import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { ModalPopupAlertComponent } from '../modal-popup-alert/modal-popup-alert.component';
import { ExcelUploadService } from '../../_services';
import { GlobalDataService } from '../global-data.service';
import { LoginUser } from '../../_models';
import * as XLSX from 'xlsx';
import { from } from 'rxjs';
import { map, mergeMap, tap, toArray } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-excel-upload',
  templateUrl: './excel-upload.component.html',
  styleUrls: ['./excel-upload.component.css']
})
export class ExcelUploadComponent implements OnInit {
  lstAccruals: any[] = [];
  ErrorResponse: any;
  RemainingContractData: any;
  RemainingContractDataCount!: number;
  output: any;
  user!: LoginUser;
  ErrorResponseCount!: number;
  SuccessCount: number = 0;
  FailureCount: number = 0;
  isUploading = false;
  uploadedRows = 0;
  totalRows = 0;
  @ViewChild('fileInput', { static: true }) fileInput: any;
  @Output() onUpload: EventEmitter<any> = new EventEmitter<any>();
  message = true;
  constructor(
    private modalService: NgbModal, private http: HttpClient, private excelservice: ExcelUploadService,
    private gd: GlobalDataService) { }

  ngOnInit() {
    this.user = this.gd.getUserData();
  }

  openAlertModal(strmsg: string) {
    const modalRef = this.modalService.open(ModalPopupAlertComponent);
    modalRef.componentInstance.strText = strmsg;
    modalRef.result.then((result) => {
    }, (reason) => {
    });
  }

  uploadFiles() {
    if (this.isUploading) {
      return;
    }

    const file = this.fileInput.nativeElement.files[0];
    if (file == null) {
      this.openAlertModal("Please choose the file to upload");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const records = sheet ? XLSX.utils.sheet_to_json(sheet) : [];

        if (records.length === 0) {
          this.openAlertModal("The selected file contains no records to upload");
          return;
        }

        this.output = [];
        this.ErrorResponse = [];
        this.ErrorResponseCount = 0;
        this.RemainingContractData = [];
        this.RemainingContractDataCount = 0;
        this.SuccessCount = 0;
        this.FailureCount = 0;
        this.message = true;
        const batchSize = 20;
        const batchCount = Math.ceil(records.length / batchSize);
        this.totalRows = records.length;
        this.uploadedRows = 0;
        this.isUploading = true;
        let completedRows = 0;

        from(Array.from({ length: batchCount }, (_, batchIndex) => batchIndex)).pipe(
          mergeMap(batchIndex => {
            const batch = records.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize);
            const formdataBatch = new FormData();
            const batchFile = this.generateExcelFile(batch);
            formdataBatch.append("file", batchFile, `batch_${batchIndex + 1}.xlsx`);

            return this.excelservice.UploadExcel(formdataBatch).pipe(
              map(result => ({ batchIndex, batchLength: batch.length, result })),
              tap(() => {
                completedRows += batch.length;
                this.uploadedRows = completedRows;
              })
            );
          }, 4),
          toArray()
        ).subscribe({
          next: results => {
            results.sort((left, right) => left.batchIndex - right.batchIndex);
            for (const { batchIndex, result } of results) {
              const batchOffset = batchIndex * batchSize;
              if (Array.isArray(result)) {
                const adjustedErrors = result
                  .filter((entry: any) => !String(entry.Key).includes("Message"))
                  .map((entry: any) => ({
                    Key: batchOffset + parseInt(entry.Key, 10),
                    Value: entry.Value
                  }));
                this.ErrorResponse = this.ErrorResponse.concat(adjustedErrors);

                const adjustedRemainingData = result
                  .filter((entry: any) => String(entry.Key).includes("Message"))
                  .map((entry: any) => {
                    const originalRowNumber = parseInt(String(entry.Key).replace("Message", ""), 10);
                    const adjustedRowNumber = batchOffset + originalRowNumber;
                    return {
                      Key: "Message" + adjustedRowNumber,
                      Value: String(entry.Value).replace(`Row${originalRowNumber}`, `Row${adjustedRowNumber}`)
                    };
                  });
                this.RemainingContractData = this.RemainingContractData.concat(adjustedRemainingData);
              } else if (result !== 0 && result !== "[]") {
                console.error("Unexpected result format:", result);
              }
              this.output = this.output.concat(result);
            }

            this.ErrorResponseCount = this.ErrorResponse.length;
            this.RemainingContractDataCount = this.RemainingContractData.length;
            this.FailureCount = this.ErrorResponseCount;
            this.SuccessCount = records.length - this.FailureCount;
            this.message = this.FailureCount > 0;
            this.onUpload.emit();
          },
          error: error => {
            this.isUploading = false;
            console.error("Excel upload failed:", error);
            this.openAlertModal("Upload failed. Some records may have been uploaded; review the list before retrying.");
          },
          complete: () => {
            this.isUploading = false;
          }
        });
      } catch (error) {
        console.error("Unable to read Excel file:", error);
        this.openAlertModal("Unable to read the selected Excel file");
      };
    }

    reader.onerror = () => this.openAlertModal("Unable to read the selected Excel file");
    reader.readAsArrayBuffer(file);
  }

  generateExcelFile(records: any[]): Blob {
    const worksheet = XLSX.utils.json_to_sheet(records);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BatchData");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }
}
